import { BodyText } from '@/components/BodyText';
import { HeaderText } from '@/components/HeaderText';
import { createUseStyles } from '@/services/createUseStyles';
import { createDateFilename, downloadCsv } from '@/utils/downloadUtils';
import { PATHS } from '@/utils/paths';
import { CheckCircle, Download, Error } from '@mui/icons-material';
import { Alert, Box, Button, Chip, Tab, Tabs } from '@mui/material';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { BulkUploadData } from '../../types';
import { createResultsStepColumns } from '../columnDefinitions';
import { MetadataBulkDataGrid } from '../MetadataBulkDataGrid';

const useStyles = createUseStyles(theme => ({
  ResultsStep_root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  ResultsStep_content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '24px',
  },
  ResultsStep_summary: {
    marginBottom: '24px',
    padding: '16px',
    backgroundColor: theme.palette.background.default,
    borderRadius: '8px',
    flexShrink: 0,
  },
  ResultsStep_tabContent: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  ResultsStep_dataGridContainer: {
    flex: 1,
    overflow: 'hidden',
  },
  ResultsStep_actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    position: 'sticky',
    bottom: 0,
    flexShrink: 0,
  },
  ResultsStep_metadataChips: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap',
  },
}));

const TabPanel = ({ children, value, index }: { children?: React.ReactNode; index: number; value: number }) => {
  if (value !== index) {
    return;
  }

  return (
    <Box
      role='tabpanel'
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      {children}
    </Box>
  );
};

export const ResultsStep = ({ data, onReset }: { data?: BulkUploadData; onReset: () => void }) => {
  const c = useStyles();
  const [tabValue, setTabValue] = useState(0);
  const { tenantShortName } = useParams<{ tenantShortName: string }>();

  if (!data) {
    return <Alert severity='error'>No data available. Please start over.</Alert>;
  }

  const results = data.results;

  if (!results) {
    return <Alert severity='error'>No results available. Please go back and run the processing step.</Alert>;
  }

  const { successful: successfulUpdates, failed: failedUpdates } = results;
  const allResults = [...successfulUpdates, ...failedUpdates];
  const totalUpdates = successfulUpdates.reduce((total, result) => total + Object.keys(result.updatedFields).length, 0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getAssetUrl = (assetId: string) => {
    if (!tenantShortName) return '#';
    return PATHS.ASSET.replace(':tenantShortName', tenantShortName).replace(':assetId', assetId);
  };

  const { successfulColumns, failedColumns, allResultsColumns } = createResultsStepColumns(getAssetUrl, data);

  const successfulRows = successfulUpdates.map((result, index) => ({
    id: `success-${index}`,
    ...result,
  }));

  const failedRows = failedUpdates.map((result, index) => ({
    id: `failed-${index}`,
    ...result,
  }));

  const allResultsRows = allResults.map((result, index) => ({
    id: `all-${index}`,
    ...result,
  }));

  const generateCSVReport = () => {
    const headers = ['Asset Name', 'Asset ID', 'Status', 'Updated Fields', 'Error Message'];
    const rows = allResults.map(result => [
      result.assetName,
      result.assetId,
      result.status,
      Object.entries(result.updatedFields)
        .map(([k, v]) => `${k}:${v}`)
        .join('; '),
      result.errorMessage || '',
    ]);

    const data = [headers, ...rows];
    const filename = createDateFilename('metadata-bulk-update-results', 'csv');

    downloadCsv(data, filename);
  };

  return (
    <Box className={c.ResultsStep_root}>
      <Box className={c.ResultsStep_content}>
        <HeaderText gutterBottom>Update Results</HeaderText>
        <Box className={c.ResultsStep_summary}>
          <HeaderText gutterBottom>Summary</HeaderText>
          <Box display='flex' gap={2} flexWrap='wrap' marginBottom={1}>
            <Chip icon={<CheckCircle />} label={`${successfulUpdates.length} Successful`} color='success' />
            <Chip icon={<Error />} label={`${failedUpdates.length} Failed`} color='error' />
          </Box>
          <BodyText>
            <strong>Total Metadata Updates Applied:</strong> {totalUpdates}
          </BodyText>
        </Box>
        {successfulUpdates.length > 0 && (
          <Alert severity='success' sx={{ marginBottom: 2 }}>
            <BodyText>Successfully updated metadata for {successfulUpdates.length} assets!</BodyText>
          </Alert>
        )}
        {failedUpdates.length > 0 && (
          <Alert severity='error' sx={{ marginBottom: 2 }}>
            <BodyText>
              {failedUpdates.length} updates failed. Please review the details below and consider retrying.
            </BodyText>
          </Alert>
        )}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={`Successful (${successfulUpdates.length})`} />
            <Tab label={`Failed (${failedUpdates.length})`} />
            <Tab label='All Results' />
          </Tabs>
        </Box>
        <Box className={c.ResultsStep_tabContent}>
          <TabPanel value={tabValue} index={0}>
            <Box className={c.ResultsStep_dataGridContainer}>
              <MetadataBulkDataGrid
                columns={successfulColumns}
                rows={successfulRows}
                rowCount={successfulRows.length}
              />
            </Box>
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <Box className={c.ResultsStep_dataGridContainer}>
              <MetadataBulkDataGrid columns={failedColumns} rows={failedRows} rowCount={failedRows.length} />
            </Box>
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <Box className={c.ResultsStep_dataGridContainer}>
              <MetadataBulkDataGrid
                columns={allResultsColumns}
                rows={allResultsRows}
                rowCount={allResultsRows.length}
              />
            </Box>
          </TabPanel>
        </Box>
      </Box>
      <Box className={c.ResultsStep_actions}>
        <Button variant='outlined' startIcon={<Download />} onClick={generateCSVReport}>
          Download Report
        </Button>
        <Button variant='contained' color='inverted' onClick={onReset}>
          Start Over
        </Button>
      </Box>
    </Box>
  );
};
